import { useEffect, useMemo, useRef } from "react";
import { tickFromFraction } from "../state/markers";
import type { ReplayPayload } from "../types";

export interface TimelineViewProps {
  replay: ReplayPayload;
  tick: number;
  onSeek: (tick: number) => void;
  focus: number | null;
  onFocus: (agent: number) => void;
}

export function TimelineView({ replay, tick, onSeek, focus, onFocus }: TimelineViewProps) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const host = useRef<HTMLDivElement>(null);

  // Rasterize once per replay: one column per tick, one row per agent, 1px cells.
  // Rows with no action that tick keep the sentinel black — the dead render black.
  const raster = useMemo(() => {
    const off = document.createElement("canvas");
    off.width = Math.max(1, replay.frames.length);
    off.height = Math.max(1, replay.population);
    const ctx = off.getContext("2d");
    if (!ctx) return off;
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, off.width, off.height);
    replay.frames.forEach((frame, column) => {
      for (const [actor, verb] of frame.actions) {
        ctx.fillStyle = replay.palette[verb] ?? "#cccccc";
        ctx.fillRect(column, actor, 1, 1);
      }
    });
    return off;
  }, [replay]);

  useEffect(() => {
    const element = canvas.current;
    const box = host.current?.getBoundingClientRect();
    const ctx = element?.getContext("2d");
    if (!element || !ctx || !box) return;
    const dpr = window.devicePixelRatio || 1;
    const cssWidth = Math.max(1, Math.floor(box.width));
    const cssHeight = Math.max(1, Math.floor(box.height));
    element.width = cssWidth * dpr;
    element.height = cssHeight * dpr;
    element.style.width = `${cssWidth}px`;
    element.style.height = `${cssHeight}px`;
    // imageSmoothingEnabled must be set AFTER any size assignment (resize clears it).
    ctx.imageSmoothingEnabled = false;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.drawImage(raster, 0, 0, cssWidth, cssHeight);
    // The shared-tick cursor — coordinates in CSS pixels.
    // Column-center semantics: tick t's cursor sits mid-column, matching the raster cells.
    const x = ((tick - 0.5) / replay.frames.length) * cssWidth;
    ctx.strokeStyle = "#e8a33d";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, cssHeight);
    ctx.stroke();
    if (focus !== null && replay.population > 0) {
      const rowTop = (focus / replay.population) * cssHeight;
      const rowHeight = Math.max(2, cssHeight / replay.population);
      ctx.strokeStyle = "rgba(232,163,61,0.9)";
      ctx.lineWidth = 1;
      ctx.strokeRect(0.5, rowTop + 0.5, cssWidth - 1, rowHeight);
    }
  }, [raster, tick, replay.frames.length, focus]);

  return (
    <div className="view">
      <span className="metric-note">
        one row per agent, one column per tick — click to jump the replay and follow that agent
      </span>
      <div className="canvas-host" ref={host} style={{ placeItems: "stretch" }}>
        <canvas
          ref={canvas}
          style={{ cursor: "crosshair", width: "100%", height: "100%" }}
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            onSeek(tickFromFraction((e.clientX - rect.left) / rect.width, replay.frames.length));
            const row = Math.floor(((e.clientY - rect.top) / rect.height) * replay.population);
            onFocus(Math.max(0, Math.min(replay.population - 1, row)));
          }}
        />
      </div>
      <div className="legend">
        {replay.verbs.map((verb) => (
          <span key={verb}>
            <span className="legend-swatch" style={{ background: replay.palette[verb] }} />
            {verb}
          </span>
        ))}
        <span>
          <span className="legend-swatch" style={{ background: "#000" }} />
          DEAD
        </span>
      </div>
    </div>
  );
}
